import { type FC, useMemo, useCallback } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { toast } from "@/components/podkit/toasts/use-toast";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { useGetOrganizationPolicies } from "@/queries/organization-queries";
import { formatSeconds } from "@/format/time";
import { formatError } from "@/utils/errors";
import { isOrgPolicyMoreRestrictive } from "@/utils/auto-delete";

type AutoDeleteOptionWithDisabled = {
    label: string;
    value: number;
    disabled?: boolean;
};

const AUTO_DELETE_ARCHIVED_ENVIRONMENTS_AFTER = "DELETE_ARCHIVED_ENVIRONMENTS_AFTER";

const AUTO_DELETE_BASE_OPTIONS: { label: string; value: number }[] = [
    { label: "1 day", value: 1 * 24 * 60 * 60 },
    { label: "2 days", value: 2 * 24 * 60 * 60 },
    { label: "3 days", value: 3 * 24 * 60 * 60 },
    { label: "5 days", value: 5 * 24 * 60 * 60 },
    { label: "1 week", value: 7 * 24 * 60 * 60 },
    { label: "2 weeks", value: 14 * 24 * 60 * 60 },
    { label: "4 weeks", value: 4 * 7 * 24 * 60 * 60 },
    { label: "never", value: 0 },
];

export const AutoDeleteSelect: FC = () => {
    const { value: userPreferenceRaw, setValueAsync: setUserPreferenceAutoDelete } = usePreferenceValue(
        AUTO_DELETE_ARCHIVED_ENVIRONMENTS_AFTER,
    );
    const { data: organizationPolicies, isLoading: isPoliciesLoading } = useGetOrganizationPolicies();

    // Get organization policy value in seconds
    const organizationPolicySeconds = useMemo(() => {
        const seconds = organizationPolicies?.deleteArchivedEnvironmentsAfter?.seconds;
        return typeof seconds === "bigint" ? Number(seconds) : undefined;
    }, [organizationPolicies]);

    // Check if the currently selected option is enforced by org policy and determine effective selection
    const { isOrgPolicyEnforced, effectiveSelectedOption } = useMemo(() => {
        const userPreference = typeof userPreferenceRaw === "string" ? parseInt(userPreferenceRaw, 10) || 0 : 0;

        if (!organizationPolicySeconds) {
            return {
                isOrgPolicyEnforced: false,
                effectiveSelectedOption: userPreference,
            };
        }

        const isEnforced = isOrgPolicyMoreRestrictive(organizationPolicySeconds, userPreference);

        return {
            isOrgPolicyEnforced: isEnforced,
            effectiveSelectedOption: isEnforced ? organizationPolicySeconds : userPreference,
        };
    }, [userPreferenceRaw, organizationPolicySeconds]);

    // Filter options based on organization policy
    const availableOptions = useMemo((): AutoDeleteOptionWithDisabled[] => {
        if (!organizationPolicySeconds) {
            // If no org policy, all base options are available
            let options = AUTO_DELETE_BASE_OPTIONS.map((option) => ({
                ...option,
                disabled: false,
            }));

            // Add custom option only if it's the selected value and not in base options
            if (!options.some((option) => option.value === effectiveSelectedOption)) {
                const formattedTime = formatSeconds(effectiveSelectedOption, "long");
                const label = `after ${formattedTime}`;

                options = [
                    ...options,
                    {
                        label,
                        value: effectiveSelectedOption,
                        disabled: false,
                    },
                ];
            }

            return options;
        }

        const baseOptions = AUTO_DELETE_BASE_OPTIONS.map((option) => ({
            ...option,
            disabled: option.value > organizationPolicySeconds || option.value === 0, // "Never" (0) is disabled when org policy exists
        }));

        // Add the effective selected option if it's not in the standard options
        if (!baseOptions.some((option) => option.value === effectiveSelectedOption)) {
            const formattedTime = formatSeconds(effectiveSelectedOption, "long");
            const label = `After ${formattedTime}`;

            return [
                ...baseOptions,
                {
                    label,
                    value: effectiveSelectedOption,
                    disabled: effectiveSelectedOption > organizationPolicySeconds,
                },
            ];
        }

        return baseOptions;
    }, [organizationPolicySeconds, effectiveSelectedOption]);

    // Memoized display text for the selected option
    const selectedOptionDisplay = useMemo(() => {
        const option = availableOptions.find((opt) => opt.value === effectiveSelectedOption);
        if (!option?.label) return "";

        // Special case for "never" option
        if (option.value === 0) {
            return "Auto-delete never";
        }

        return `Auto-delete after ${option.label}`;
    }, [availableOptions, effectiveSelectedOption]);

    const handleOptionChange = useCallback(
        async (value: string) => {
            const newOption = parseInt(value, 10);

            // If clicking the org policy value and it's enforced, do nothing
            if (isOrgPolicyEnforced && newOption === organizationPolicySeconds) {
                return;
            }

            try {
                await setUserPreferenceAutoDelete(newOption.toString());

                // Get the selected option label for the toast message
                const selectedOption = availableOptions.find((opt) => opt.value === newOption);
                const description =
                    newOption === 0
                        ? "Your archived environments will no longer be deleted automatically!"
                        : `Your archived environments will be automatically deleted after ${selectedOption?.label}!`;

                toast({
                    title: "Auto-delete preference updated",
                    description,
                });
            } catch (error) {
                toast({
                    title: "Failed to update preference",
                    description: formatError(error),
                });
            }
        },
        [isOrgPolicyEnforced, organizationPolicySeconds, setUserPreferenceAutoDelete, availableOptions],
    );

    return (
        <Select
            name="auto-delete-archived-environment"
            value={effectiveSelectedOption.toString()}
            onValueChange={handleOptionChange}
            aria-label="Auto-delete archived environments preference"
        >
            <SelectTrigger
                loading={isPoliciesLoading}
                className="w-fit border-0 bg-transparent py-0 pl-2 hover:bg-transparent focus:bg-transparent"
                data-testid="auto-delete-archived-environment-select"
            >
                <SelectValue>
                    <span>{selectedOptionDisplay}</span>
                </SelectValue>
            </SelectTrigger>
            <SelectContent className="w-[calc(var(--radix-select-trigger-width)*0.6)]" alignOffset={100}>
                {availableOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value.toString()} disabled={option.disabled}>
                        <div
                            className={`flex w-full items-center justify-between pl-2 text-base ${
                                option.disabled ? "text-content-tertiary" : "text-content-primary"
                            }`}
                        >
                            <span>{option.value === 0 ? "Never" : `After ${option.label}`}</span>
                        </div>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
};
